import React, { useReducer, useEffect, useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import Logger from '@lmig/dotcom-utilities-helpers/logger';
import affinitySpec from '@lmig/dotcom-aspect-helpers/specs/affinity.spec';
import agentSpec from '@lmig/dotcom-aspect-helpers/specs/agent.spec';
import campaignSpec from '@lmig/dotcom-aspect-helpers/specs/campaign.spec';
import officeSpec from '@lmig/dotcom-aspect-helpers/specs/office.spec';
import phoneSpec from '@lmig/dotcom-aspect-helpers/specs/phone.spec';

const logger = new Logger('client:context:contextapp');

const ContextApp = React.createContext();

const DEFAULT_LOCATION = { state: undefined, city: undefined, zipCode: undefined };
const DEFAULT_EMPTY_ARRAY = [];

const defaultEntitySchema = { active: false, queryType: '', queryValue: '' };

const AppContextProvider = ({
  inclusions = DEFAULT_EMPTY_ARRAY,
  exclusions = DEFAULT_EMPTY_ARRAY,
  campaign = defaultEntitySchema,
  affinity = defaultEntitySchema,
  agent = defaultEntitySchema,
  indAgent = defaultEntitySchema,
  office = defaultEntitySchema,
  replacementPhoneNumbers = DEFAULT_EMPTY_ARRAY,
  defaultContactPhone = '',
  phones: propPhones = {},
  host = '',
  jurisdictionPage: propJurisdictionPage = '',
  location: propLocation = DEFAULT_LOCATION,
  children,
}) => {
  const [hasCheckedLocation, setHasCheckedLocation] = useState(false);
  const [phones, setPhones] = useState(propPhones);
  const [location, setLocation] = useState(propLocation);
  const [jurisdictionPage, setJurisdictionPage] = useState(propJurisdictionPage);

  // Entity state object.
  // This state's reducer merges entity objects together preferring objects with values.
  const [entities, dispatchEntities] = useReducer(
    (state, newState) => {
      const objsToUpdate = Object.entries(newState).reduce((acc, [key, value]) => {
        if (value === defaultEntitySchema) {
          return acc;
        }

        return {
          ...acc,
          [key]: value,
        };
      }, {});

      return {
        ...state,
        ...objsToUpdate,
      };
    },
    {
      campaign,
      affinity,
      agent,
      indAgent,
      office,
    },
  );

  // Ensure we update entity state when a page render occurs and
  //   changes the relevant props.
  useEffect(() => {
    dispatchEntities({
      campaign,
      affinity,
      agent,
      indAgent,
      office,
    });
  }, [campaign, affinity, agent, indAgent, office]);

  const [restrictions, dispatchRestrictions] = useReducer(
    (state, action) => {
      switch (action.type) {
        case 'SET_INCLUSIONS':
          return {
            ...state,
            inclusions: [...action.inclusions],
          };
        case 'SET_EXCLUSIONS':
          return {
            ...state,
            exclusions: [...action.exclusions],
          };
        default:
          return {
            inclusions: [...new Set([...state.inclusions, ...action.inclusions])],
            exclusions: [...new Set([...state.exclusions, ...action.exclusions])],
          };
      }
    },
    {
      inclusions,
      exclusions,
    },
  );

  // Ensure we update restriction state when a page render occurs and
  //   changes the relevant props.
  useEffect(() => {
    dispatchRestrictions({ inclusions, exclusions });
  }, [inclusions, exclusions]);

  const getAgentData = useCallback(() => {
    const { agent: curAgent } = entities;

    if (curAgent.active) {
      logger.debug(`Returning agent data ${JSON.stringify(curAgent)}`);

      return curAgent;
    }

    return {};
  }, [entities]);

  const setEntityPersonalization = ({ personalize: { phones: dataPhones, tags } }) => {
    setPhones(dataPhones);
    dispatchRestrictions(tags);
  };

  const memoValue = useMemo(() => ({
    ...restrictions,
    entities,
    replacementPhoneNumbers,
    defaultContactPhone,
    phones,
    location,
    jurisdictionPage,
    setLocation,
    setJurisdictionPage,
    getAgentData,
    dispatchRestrictions,
    dispatchEntities,
    setEntityPersonalization,
    setPhones,
    hasCheckedLocation,
    setHasCheckedLocation,
    host,
  }), [
    defaultContactPhone,
    entities,
    getAgentData,
    host,
    jurisdictionPage,
    location,
    phones,
    replacementPhoneNumbers,
    restrictions,
    hasCheckedLocation,
  ]);

  return (
    <ContextApp.Provider value={memoValue}>
      {children}
    </ContextApp.Provider>
  );
};

AppContextProvider.propTypes = {
  affinity: PropTypes.shape(affinitySpec),
  agent: PropTypes.shape(agentSpec),
  campaign: PropTypes.shape(campaignSpec),
  office: PropTypes.shape(officeSpec),
  jurisdictionPage: PropTypes.string,
  indAgent: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  phones: PropTypes.shape(phoneSpec),
  location: PropTypes.shape({
    state: PropTypes.string,
    city: PropTypes.string,
    zipCode: PropTypes.string,
  }),
  inclusions: PropTypes.arrayOf(PropTypes.string),
  exclusions: PropTypes.arrayOf(PropTypes.string),
  replacementPhoneNumbers: PropTypes.arrayOf(
    PropTypes.shape({
      original: PropTypes.string,
      replacement: PropTypes.string,
      scope: PropTypes.string,
    }),
  ),
  defaultContactPhone: PropTypes.string,
  host: PropTypes.string,
  children: PropTypes.node.isRequired,
};

// Then make a consumer which will surface it
const AppContextConsumer = ContextApp.Consumer;

export { AppContextProvider, AppContextConsumer, ContextApp };
